import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Chip,
  Typography,
  Box,
  IconButton,
  Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import FeedIcon from "@mui/icons-material/Feed";
import PersonIcon from "@mui/icons-material/Person";
import { getAllUsers } from "../../resources/api.resource.ts";
import { MemberOption } from "../../models/User.ts";
import store from "../../utils/store.ts";
import Notification from "./Notification.tsx";

interface InputDialogProps {
  open: boolean;
  title: string;
  label: string;
  onSubmit: (name: string, members: string[]) => void;
  onClose: () => void;
  hideNameInput?: boolean;
}


const InputDialog: React.FC<InputDialogProps> = ({ open, title, label, onSubmit, onClose, hideNameInput }) => {
  const [name, setName] = useState("");
  const [options, setOptions] = useState<MemberOption[]>([]);
  const [members, setMembers] = useState<(MemberOption | string)[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [notif, setNotif] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "info" | "warning" | "error",
  });
  useEffect(() => {
    let isMounted = true;
    const fetchDataAsync = async () => {
      try {
        const allMembers = store.getState().allUsers;
        if (isMounted && Array.isArray(allMembers)) {
          const currentUserId = store.getState().user?.userId?.toLowerCase();
          setOptions(allMembers.filter(m => m.uid?.toLowerCase() !== currentUserId));
        }
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };
    fetchDataAsync();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = () => {
    
    const memberIds = members.map((member) =>
      typeof member === "string" ? member : member.uid
    );

    if (memberIds.length == 0) {
      setNotif({
        open: true,
        message: "Please select at least one member.",
        severity: "warning",
      });
    } else {
      const finalName = hideNameInput ? "Direct Message" : name.trim();
      onSubmit(finalName, memberIds);
      setName("");
      setMembers([]);
    }
    
  };


  // Determine which icon to show based on title
  const getIconForTitle = () => {
    if (title.includes("Group")) return <GroupsIcon sx={{ fontSize: 28, color: '#0857c3' }} />;
    if (title.includes("Project")) return <FeedIcon sx={{ fontSize: 28, color: '#0857c3' }} />;
    return <PersonIcon sx={{ fontSize: 28, color: '#0857c3' }} />;
  };

  const getDescriptionText = () => {
    if (title.includes("Group")) return "Create a new group to collaborate with multiple members";
    if (title.includes("Project")) return "Create a new project to organize your work";
    return "Start a direct message conversation with a team member or assistant";
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '10px',
              backgroundColor: 'rgba(8, 87, 195, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {getIconForTitle()}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2a2e42', fontSize: '1.25rem' }}>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem', mt: 0.5 }}>
                {getDescriptionText()}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#999',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                color: '#666'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
      </DialogTitle>

      <DialogContent sx={{ p: '24px !important', pt: '32px !important' }}>
        {!hideNameInput && (
          <TextField
            autoFocus
            margin="normal"
            label={label}
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            sx={{
              mt: 0,
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover fieldset': {
                  borderColor: '#0857c3',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0857c3',
                  borderWidth: '2px'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#0857c3'
              }
            }}
          />
        )}

        <Autocomplete
          sx={{
            mt: hideNameInput ? 0 : 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0857c3',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0857c3',
                borderWidth: '2px'
              }
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#0857c3'
            }
          }}
          multiple
          freeSolo
          options={options}
          value={members}
          onChange={(event, newValue) => {
            setMembers(newValue);
          }}
          filterOptions={(options, params) => {
            const filtered = options.filter(
              (option): option is MemberOption =>
                typeof option !== "string" &&
                option.name
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase())
            );

            if (
              params.inputValue !== "" &&
              !filtered.some(
                (opt) =>
                  opt.name.toLowerCase() === params.inputValue.toLowerCase()
              )
            ) {
              filtered.push({
                name: params.inputValue,
                email: params.inputValue,
                uid: params.inputValue,
                userType: 'user',
              });
            }
            return filtered;
          }}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : option.userType === 'agent' && option.baseModel
              ? `${option.name} (${option.baseModel})`
              : option.name
          }
          isOptionEqualToValue={(option, value) =>
            typeof option === "string" || typeof value === "string"
              ? option === value
              : option.uid === value.uid
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={
                  typeof option === "string"
                    ? option
                    : option.userType === 'agent' && option.baseModel
                    ? `${option.name} (${option.baseModel})`
                    : `${option.name}`
                }
                {...getTagProps({ index })}
                sx={{
                  borderRadius: '6px',
                  backgroundColor: 'rgba(8, 87, 195, 0.08)',
                  color: '#0857c3',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: '#0857c3',
                    '&:hover': {
                      color: '#064a9c'
                    }
                  }
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              label="Select Members"
              placeholder={members.length === 0 ? "Search by name or email..." : "Add more members"}
              helperText="Select team members or assistants to add to the conversation"
            />
          )}
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: '#666',
            px: 3,
            py: 1,
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!hideNameInput && !name.trim()}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            backgroundColor: '#0857c3',
            px: 3,
            py: 1,
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(8, 87, 195, 0.24)',
            '&:hover': {
              backgroundColor: '#064a9c',
              boxShadow: '0 4px 12px rgba(8, 87, 195, 0.32)'
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#999'
            }
          }}
        >
          Create
        </Button>
      </DialogActions>

      <Notification
        {...notif}
        onClose={() => setNotif({ ...notif, open: false })}
      />
    </Dialog>
  );
};

export default InputDialog;
